// assets/page-wishlist.js
(() => {
  const STORAGE_KEY = "tf_wishlist";

  const read = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const buildPageUrl = (page) => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", String(page));
    return url.pathname + url.search + url.hash;
  };

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;

      // core UI
      this.grid = this.querySelector("[data-wishlist-grid]");
      this.empty = this.querySelector("[data-wishlist-empty]");
      this.loading = this.querySelector("[data-wishlist-loading]");

      // pagination markup (reuse theme HTML/CSS)
      this.pagiWrap = this.querySelector("[data-wishlist-pagination]");
      this.pagiLink = this.querySelector("[data-pagi-link]");
      this.pagiParts = this.querySelector("[data-pagi-parts]");
      this.pagiLoadmore = this.querySelector("[data-pagi-loadmore]");
      this.pagiInfinite = this.querySelector("[data-pagi-infinite]");

      this.btnLoadmore = this.pagiLoadmore?.querySelector("button") || null;
      this.btnInfinite = this.pagiInfinite?.querySelector("button") || null;

      // config
      this.limit = parseInt(this.dataset.limit || "100", 10);
      this.perPage = parseInt(this.dataset.perPage || "12", 10);
      this.view = this.dataset.view || "wishlist-card";

      // mode normalize: number -> link, load_more -> loadmore
      this.pagiType = (
        this.pagiWrap?.dataset.paginationType ||
        this.dataset.mode ||
        "link"
      ).toLowerCase();
      if (this.pagiType === "number") this.pagiType = "link";
      this.pagiType = this.pagiType.replace("_", "");

      // state
      this._handlesAll = [];
      this._page = this.getPageFromURL();
      this._io = null;
      this._isLoadingMore = false;

      // initial: hide pagination until first render done
      if (this.pagiWrap) this.pagiWrap.style.display = "none";
      this.hideAllPaginationBlocks();
      this.setPaginationLoading(false);

      // events
      this.onChanged = () => this.reset(true);
      window.addEventListener("wishlist:changed", this.onChanged);

      // click load more
      if (this.btnLoadmore) {
        this.onLoadMore = (e) => {
          e.preventDefault();
          if (this._isLoadingMore) return;

          const total = this.totalPages();
          if (this._page >= total) return;

          this._page += 1;
          this.appendPage(this._page);
        };
        this.btnLoadmore.addEventListener("click", this.onLoadMore);
      }

      // click number links (delegation)
      if (this.pagiParts) {
        this.pagiParts.addEventListener("click", (e) => {
          const a = e.target.closest("a.pagination-item");
          if (!a) return;
          e.preventDefault();

          const page = parseInt(a.dataset.page || "1", 10);
          if (!Number.isFinite(page)) return;

          this._page = page;
          window.history.replaceState({}, "", buildPageUrl(page));
          this.renderLinkPage(page);
        });
      }

      this.reset(false);
    }

    disconnectedCallback() {
      window.removeEventListener("wishlist:changed", this.onChanged);
      if (this.btnLoadmore) this.btnLoadmore.removeEventListener("click", this.onLoadMore);
      this.disconnectInfinite();
    }

    // ---------------- helpers ----------------
    getPageFromURL() {
      const url = new URL(window.location.href);
      const p = parseInt(url.searchParams.get("page") || "1", 10);
      return Number.isFinite(p) && p > 0 ? p : 1;
    }

    totalPages() {
      return Math.max(1, Math.ceil(this._handlesAll.length / this.perPage));
    }

    setLoading(v) {
      if (this.loading) this.loading.style.display = v ? "" : "none";
    }

    setEmpty(v) {
      if (this.empty) this.empty.style.display = v ? "" : "none";
    }

    setPaginationLoading(isLoading) {
      // requirement: add/remove class load every time loading
      if (this.btnLoadmore) this.btnLoadmore.classList.toggle("load", isLoading);
      if (this.btnInfinite) this.btnInfinite.classList.toggle("load", isLoading);
    }

    hideAllPaginationBlocks() {
      if (this.pagiLink) this.pagiLink.style.display = "none";
      if (this.pagiLoadmore) this.pagiLoadmore.style.display = "none";
      if (this.pagiInfinite) this.pagiInfinite.style.display = "none";
    }

    showPaginationBlock(type) {
      this.hideAllPaginationBlocks();
      if (type === "link" && this.pagiLink) this.pagiLink.style.display = "";
      if (type === "loadmore" && this.pagiLoadmore) this.pagiLoadmore.style.display = "";
      if (type === "infinite" && this.pagiInfinite) this.pagiInfinite.style.display = "";
    }

    async fetchCard(handle) {
      const res = await fetch(`/products/${handle}?view=${this.view}`, {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });
      if (!res.ok) throw new Error("Fail " + handle);
      return res.text();
    }

    async renderSlice(start, end, { append } = { append: false }) {
      const slice = this._handlesAll.slice(start, end);
      if (!slice.length) return;

      this.setLoading(true);

      const html = [];
      for (const h of slice) {
        try {
          html.push(await this.fetchCard(h));
        } catch { }
      }

      if (append) this.grid.insertAdjacentHTML("beforeend", html.join(""));
      else this.grid.innerHTML = html.join("");

      this.setLoading(false);
      this.setEmpty(this.grid.children.length === 0);
    }

    // ---------------- number/link pagination ----------------
    buildParts(total, current) {
      const parts = [];
      const windowSize = 5;
      const half = Math.floor(windowSize / 2);

      let start = Math.max(1, current - half);
      let end = Math.min(total, start + windowSize - 1);
      start = Math.max(1, end - windowSize + 1);

      if (start > 1) {
        parts.push({ title: "1", page: 1, is_link: true });
        if (start > 2) parts.push({ title: "…", is_link: false });
      }

      for (let p = start; p <= end; p++) {
        parts.push({ title: String(p), page: p, is_link: p !== current });
      }

      if (end < total) {
        if (end < total - 1) parts.push({ title: "…", is_link: false });
        parts.push({ title: String(total), page: total, is_link: total !== current });
      }

      return parts;
    }

    renderLinkNav() {
      if (!this.pagiParts) return;

      const total = this.totalPages();
      const current = clamp(this._page, 1, total);

      if (total <= 1) {
        if (this.pagiLink) this.pagiLink.style.display = "none";
        return;
      }

      const prev = current > 1 ? current - 1 : null;
      const next = current < total ? current + 1 : null;
      const parts = this.buildParts(total, current);

      const li = [];

      if (prev) {
        li.push(`
          <li>
            <a href="${buildPageUrl(prev)}"
               data-page="${prev}"
               class="pagination-item pagination-item-prev btn-underline"
               aria-label="Previous">
              Previous
            </a>
          </li>
        `);
      }

      for (const part of parts) {
        if (part.is_link) {
          li.push(`
            <li>
              <a href="${buildPageUrl(part.page)}"
                 data-page="${part.page}"
                 class="pagination-item link"
                 aria-label="Page ${part.title}">
                ${part.title}
              </a>
            </li>
          `);
        } else {
          if (String(part.title) === String(current)) {
            li.push(`
              <li>
                <span class="pagination-item pagination-item-current"
                      aria-current="page"
                      aria-label="Page ${part.title}">
                  ${part.title}
                </span>
              </li>
            `);
          } else {
            li.push(`
              <li>
                <span class="pagination-item">${part.title}</span>
              </li>
            `);
          }
        }
      }

      if (next) {
        li.push(`
          <li>
            <a href="${buildPageUrl(next)}"
               data-page="${next}"
               class="pagination-item pagination-item-next btn-underline"
               aria-label="Next">
              Next
            </a>
          </li>
        `);
      }

      this.pagiParts.innerHTML = li.join("");
    }

    // ---------------- loadmore/infinite next visibility ----------------
    updateNextUrlAndVisibility() {
      const total = this.totalPages();
      const current = clamp(this._page, 1, total);
      const next = current < total ? current + 1 : null;

      const nextUrl = next ? buildPageUrl(next) : "";

      if (this.btnLoadmore) this.btnLoadmore.setAttribute("data-next-url", nextUrl);
      if (this.btnInfinite) this.btnInfinite.setAttribute("data-next-url", nextUrl);

      // IMPORTANT: keep button visible while loading; hide ONLY if no next page.
      if (!next) {
        if (this.pagiLoadmore) this.pagiLoadmore.style.display = "none";
        if (this.pagiInfinite) this.pagiInfinite.style.display = "none";
      }
    }

    // ---------------- infinite observer ----------------
    setupInfinite() {
      this.disconnectInfinite();
      if (this.pagiType !== "infinite") return;

      const sentinel = this.btnInfinite || this.pagiInfinite;
      if (!sentinel) return;

      this._io = new IntersectionObserver(
        (entries) => {
          const hit = entries.some((en) => en.isIntersecting);
          if (!hit) return;

          const total = this.totalPages();
          if (this._page >= total) return;
          if (this._isLoadingMore) return;

          this._page += 1;
          this.appendPage(this._page);
        },
        { root: null, rootMargin: "250px 0px", threshold: 0.01 }
      );

      this._io.observe(sentinel);
    }

    disconnectInfinite() {
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
    }

    // ---------------- render flows ----------------
    async renderLinkPage(page) {
      const total = this.totalPages();
      const p = clamp(page, 1, total);
      this._page = p;

      // DO NOT hide pagination on number click (avoid flicker)
      // only update list + nav
      const start = (p - 1) * this.perPage;
      const end = start + this.perPage;

      this.setPaginationLoading(false);
      await this.renderSlice(start, end, { append: false });

      // ensure pagination visible after render
      this.showPaginationBlock("link");
      this.renderLinkNav();

      if (this.pagiWrap) this.pagiWrap.style.display = "";
      this.disconnectInfinite();
    }

    async appendPage(page) {
      const total = this.totalPages();
      const p = clamp(page, 1, total);
      this._page = p;

      const end = p * this.perPage;
      const start = this.grid.children.length;

      this._isLoadingMore = true;
      this.setPaginationLoading(true);

      await this.renderSlice(start, end, { append: true });

      this._isLoadingMore = false;
      this.setPaginationLoading(false);

      // show pagination after items are rendered
      this.showPaginationBlock(this.pagiType);
      this.updateNextUrlAndVisibility();

      if (this.pagiWrap) this.pagiWrap.style.display = "";

      if (this.pagiType === "infinite") this.setupInfinite();
      else this.disconnectInfinite();
    }

    async reset(fromEvent) {
      if (!this.grid) return;

      this._handlesAll = read().slice(0, this.limit);
      this.grid.innerHTML = "";

      // hide pagination only for initial/reset load until items render
      if (this.pagiWrap) this.pagiWrap.style.display = "none";
      this.hideAllPaginationBlocks();
      this.setPaginationLoading(false);

      this.setEmpty(false);

      if (!this._handlesAll.length) {
        this.setLoading(false);
        this.setEmpty(true);
        this.disconnectInfinite();
        return;
      }

      // choose initial page
      if (!fromEvent && this.pagiType === "link") {
        this._page = this.getPageFromURL();
      } else if (this.pagiType !== "link") {
        this._page = 1;
      } else {
        this._page = this._page || 1;
      }

      // render by mode
      if (this.pagiType === "link") {
        await this.renderLinkPage(this._page);
      } else {
        this.showPaginationBlock(this.pagiType); // keep block visible
        await this.appendPage(this._page);
      }
    }
  }

  if (!customElements.get("wishlist-page")) {
    customElements.define("wishlist-page", WishlistPage);
  }
})();
